import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { Smile } from "lucide-react";
import {
  HiOutlineHome,
  HiOutlineShoppingBag,
  HiOutlineUser,
  HiOutlineUserCircle,
} from "react-icons/hi";
import {
  HiOutlineRectangleGroup,
  HiOutlineShoppingCart,
  HiOutlineChatBubbleLeftRight,
  HiOutlineArrowRightOnRectangle,
  HiOutlineChevronDown,
} from "react-icons/hi2";
import { HiOutlineBars3, HiOutlineXMark } from "react-icons/hi2";
import SummaryApi from "../common";
import logo from '../assets/logo.png'

const Header = () => {
  const [userData, setUserData] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile menu when location changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Close dropdown/menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getUserId = useCallback(() => {
    const sessionData = sessionStorage.getItem("user");
    if (!sessionData) return null;
    try {
      if (sessionData.startsWith("{") || sessionData.startsWith("[")) {
        const parsed = JSON.parse(sessionData);
        return parsed.user_id || parsed.id || parsed;
      }
      return sessionData;
    } catch (e) {
      return sessionData;
    }
  }, []);

  const fetchCartCount = useCallback(async () => {
    const userId = getUserId();
    if (!userId) {
      setCartCount(0);
      return;
    }
    try {
      const response = await fetch(SummaryApi.getCartById.url, {
        method: SummaryApi.getCartById.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await response.json();
      if (result.status && result.data) setCartCount(result.data.length);
    } catch (error) {
      console.error("Cart count error:", error);
    }
  }, [getUserId]);

  const fetchUserProfile = async (id) => {
    try {
      const response = await fetch(SummaryApi.userDetailsById.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: id }),
      });
      const result = await response.json();
      if (result.status === "success" || result.data) {
        setUserData(result.data || result.user);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const userId = getUserId();
    if (userId) {
      fetchUserProfile(userId);
      fetchCartCount();
    } else {
      setUserData(null);
      setCartCount(0);
    }
    const handleCartRefresh = () => fetchCartCount();
    window.addEventListener("cartUpdate", handleCartRefresh);
    return () => window.removeEventListener("cartUpdate", handleCartRefresh);
  }, [location, fetchCartCount, getUserId]);

  const handleLogout = () => {
    sessionStorage.removeItem("user");
    setUserData(null);
    setCartCount(0);
    setIsDropdownOpen(false);
    toast.dismiss("logout-confirm");

    toast.custom((t) => (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={t.visible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
        className="max-w-md w-full bg-white shadow-2xl rounded-3xl p-4 border-b-4 border-rose-500 flex items-center gap-4 z-[9999]"
      >
        <div className="h-12 w-12 rounded-xl bg-rose-50 flex items-center justify-center text-rose-500">
          <Smile size={28} />
        </div>
        <div>
          <p className="text-sm font-bold text-rose-950">See you soon! 👋</p>
          <p className="text-xs text-rose-900/50">Logged out successfully.</p>
        </div>
      </motion.div>
    ), { duration: 2000 });

    navigate("/login");
  };

  const confirmLogout = () => {
    setIsDropdownOpen(false);
    toast.custom((t) => (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-sm w-full bg-white shadow-2xl rounded-3xl p-6 border-b-4 border-rose-500 mt-20"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="h-12 w-12 rounded-2xl bg-rose-50 flex items-center justify-center text-rose-500">
            <HiOutlineArrowRightOnRectangle size={24} />
          </div>
          <p className="text-base font-bold text-rose-950">Logging Out?</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => handleLogout()} className="flex-1 bg-rose-500 text-white font-bold py-3 rounded-xl">Yes, Logout</button>
          <button onClick={() => toast.dismiss("logout-confirm")} className="flex-1 bg-gray-100 text-gray-500 font-bold py-3 rounded-xl">Cancel</button>
        </div>
      </motion.div>
    ), { id: "logout-confirm", duration: Infinity });
  };

  return (
    <header className="sticky top-0 z-[50] bg-white/90 backdrop-blur-xl border-b border-rose-100">
      <div className="mx-auto px-4 lg:px-8 py-3 flex items-center justify-between">
        
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src={logo} className="w-16 md:w-20 object-contain" alt="Logo" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          <NavLink to="/" icon={<HiOutlineHome size={18} />} label="Home" active={location.pathname === "/"} />
          <NavLink to="/products" icon={<HiOutlineShoppingBag size={18} />} label="Shop" active={location.pathname === "/products"} />
          <NavLink to="/packages" icon={<HiOutlineRectangleGroup size={18} />} label="Subscribe" active={location.pathname === "/packages"} />
          <NavLink to="/sakhi-sponsor" icon={<HiOutlineChatBubbleLeftRight size={18} />} label="Sakhi Sponsor" active={location.pathname === "/sakhi-sponsor"} />
          <NavLink to="/contact" icon={<HiOutlineChatBubbleLeftRight size={18} />} label="Help" active={location.pathname === "/contact"} />
        </nav>

        {/* Right Section: User + Cart + Hamburger */}
        <div className="flex items-center gap-2 md:gap-4">
          
          {/* User Profile */}
          {userData ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 p-1 md:pr-3 bg-rose-50/50 hover:bg-rose-50 rounded-2xl transition-all border border-rose-100"
              >
                <div className="h-8 w-8 md:h-9 md:w-9 bg-rose-500 rounded-xl flex items-center justify-center text-white font-bold shadow-sm">
                  {userData.user_name?.charAt(0).toUpperCase()}
                </div>
                <span className="hidden md:block text-xs font-black text-rose-950 uppercase">
                  {userData.user_name.split(" ")[0]}
                </span>
                <HiOutlineChevronDown className={`hidden md:block transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-3 w-52 bg-white border border-rose-100 rounded-2xl shadow-xl p-2"
                  >
                    <DropdownLink to="/profile" icon={<HiOutlineUserCircle />} label="My Profile" onClick={() => setIsDropdownOpen(false)} />
                    <DropdownLink to="/my-orders" icon={<HiOutlineRectangleGroup />} label="My Orders" onClick={() => setIsDropdownOpen(false)} />
                    <DropdownLink to="/my-subscriptions" icon={<HiOutlineChatBubbleLeftRight />} label="Subscriptions" onClick={() => setIsDropdownOpen(false)} />
                    <button onClick={confirmLogout} className="w-full flex items-center gap-3 p-3 rounded-xl text-rose-950/60 hover:text-rose-600 hover:bg-rose-50 transition-all font-bold text-sm">
                      <HiOutlineArrowRightOnRectangle size={20} /> Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Link to="/login" className="p-2 text-rose-400 hover:bg-rose-50 rounded-xl">
              <HiOutlineUser size={24} />
            </Link>
          )}

          {/* Cart Icon */}
          <Link to="/cart" className="p-2 text-rose-400 hover:bg-rose-50 rounded-xl relative">
            <HiOutlineShoppingCart size={24} />
            {cartCount > 0 && (
              <span className="absolute top-1 right-1 bg-rose-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center border-2 border-white">
                {cartCount}
              </span>
            )}
          </Link>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
          >
            {isMobileMenuOpen ? <HiOutlineXMark size={28} /> : <HiOutlineBars3 size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-rose-900/20 backdrop-blur-sm lg:hidden z-[-1]"
            />
            {/* Menu Links */}
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden bg-white border-b border-rose-100 overflow-hidden shadow-inner"
            >
              <nav className="flex flex-col p-4 gap-2">
                <MobileNavLink to="/" label="Home" icon={<HiOutlineHome />} active={location.pathname === "/"} />
                <MobileNavLink to="/products" label="Shop" icon={<HiOutlineShoppingBag />} active={location.pathname === "/products"} />
                <MobileNavLink to="/packages" label="Subscribe" icon={<HiOutlineRectangleGroup />} active={location.pathname === "/packages"} />
                <MobileNavLink to="/sakhi-sponsor" label="Sakhi Sponsor" icon={<HiOutlineChatBubbleLeftRight />} active={location.pathname === "/sakhi-sponsor"} />
                <MobileNavLink to="/contact" label="Help" icon={<HiOutlineChatBubbleLeftRight />} active={location.pathname === "/contact"} />
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

// Sub-components for cleaner code
const NavLink = ({ to, icon, label, active }) => (
  <Link
    to={to}
    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${
      active
        ? "bg-rose-500 text-white shadow-md shadow-rose-200"
        : "text-rose-950/40 hover:text-rose-600 hover:bg-rose-50"
    }`}
  >
    {icon} {label}
  </Link>
);

const MobileNavLink = ({ to, icon, label, active }) => (
  <Link
    to={to}
    className={`flex items-center gap-4 p-4 rounded-2xl font-bold transition-all ${
      active ? "bg-rose-500 text-white" : "bg-rose-50/50 text-rose-950/70"
    }`}
  >
    {React.cloneElement(icon, { size: 22 })}
    {label}
  </Link>
);

const DropdownLink = ({ to, icon, label, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center gap-3 p-3 rounded-xl text-rose-950/60 hover:text-rose-600 hover:bg-rose-50 transition-all font-bold text-sm"
  >
    {React.cloneElement(icon, { size: 20 })}
    {label}
  </Link>
);

export default Header;